import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAddAlert } from '@/hooks/useAddAlert'
import { useTaskTrackerBoardAccessibleStickers } from '../../../../../lib/hooks/useTaskTrackerBoardAccessibleStickers'
import {
  getTaskTrackerStickerNormalizedValue,
  normalizeStickerValueForApi,
  normalizeTaskTrackerStickerValueForPersist,
  persistTaskTrackerStickerValue,
} from '../../../../../lib/taskTrackerStickerSave.utils'
import { mergeTaskCardAfterStickerSave } from '../../../../../lib/taskTrackerTaskStickers.utils'
import { normalizeTaskDeadline } from '../../../../../lib/taskTrackerTaskInfo.utils'
import {
  TaskTrackerBoardCard,
  TaskTrackerFilterSelectOption,
} from '../../../../../store/taskTracker.types'

export type StickerFieldRow = {
  fieldSettingId: number
  fieldType: string
  systemKey?: string | null
  title: string
  color: string
  value: string | string[] | null
  options: TaskTrackerFilterSelectOption[]
  crmLeadId?: number | null
  crmFunnelId?: number | null
}

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) return error.message
  return fallback
}

const buildSavedValues = (
  task: TaskTrackerBoardCard,
  boardRows: Array<{ fieldSettingId: number; fieldType: string }>,
) => {
  const values: Record<number, string | null> = {}

  task.stickers.forEach((sticker) => {
    values[sticker.fieldSettingId] =
      sticker.fieldType === 'deadline'
        ? normalizeTaskDeadline(task.deadline) ?? sticker.value
        : sticker.value
  })

  boardRows.forEach((row) => {
    if (row.fieldType !== 'deadline') return

    values[row.fieldSettingId] =
      normalizeTaskDeadline(task.deadline) ?? values[row.fieldSettingId] ?? null
  })

  return values
}

export const useTaskStickerFieldsDraft = (
  task: TaskTrackerBoardCard | null,
  onTaskUpdated?: (task: TaskTrackerBoardCard) => void,
) => {
  const addAlert = useAddAlert()
  const boardId = task?.boardId ?? null
  const { rows, loading, error } = useTaskTrackerBoardAccessibleStickers(
    boardId,
    task != null,
  )
  const [values, setValues] = useState<Record<number, string | null>>({})
  const [savingFieldIds, setSavingFieldIds] = useState<Record<number, boolean>>(
    {},
  )

  const visibleRows = useMemo(
    () =>
      rows.filter(
        (row) => row.isVisible,
      ),
    [rows],
  )

  const fieldRows = useMemo<StickerFieldRow[]>(() => {
    return visibleRows.map((row) => {
      const taskSticker = task?.stickers.find(
        (sticker) => sticker.fieldSettingId === row.fieldSettingId,
      )

      return {
        fieldSettingId: row.fieldSettingId,
        fieldType: row.fieldType,
        systemKey: row.systemKey,
        title: row.title,
        color: row.color,
        value:
          row.fieldType === 'performer'
            ? task?.performerIds.map(String) ?? []
            : values[row.fieldSettingId] ?? null,
        options: row.options,
        crmLeadId: taskSticker?.crmLeadId ?? null,
        crmFunnelId: taskSticker?.crmFunnelId ?? null,
      }
    })
  }, [task?.performerIds, task?.stickers, values, visibleRows])

  useEffect(() => {
    if (!task) {
      setValues({})
      return
    }

    setValues(buildSavedValues(task, visibleRows))
  }, [task, visibleRows])

  const isFieldSaving = useCallback(
    (fieldSettingId: number) => Boolean(savingFieldIds[fieldSettingId]),
    [savingFieldIds],
  )

  const saveStickerValue = useCallback(
    async (
      fieldSettingId: number,
      nextValue: string | null,
      options?: {
        displayValue?: string | null
        crmLeadId?: number | null
        crmFunnelId?: number | null
      },
    ) => {
      if (!task?.id || savingFieldIds[fieldSettingId]) return false

      const stickerRow = visibleRows.find(
        (row) => row.fieldSettingId === fieldSettingId,
      )
      const fieldType = stickerRow?.fieldType ?? 'text'
      const normalizedNext = normalizeTaskTrackerStickerValueForPersist(
        fieldType,
        nextValue,
      )
      const normalizedCurrent = getTaskTrackerStickerNormalizedValue(
        task,
        fieldSettingId,
        fieldType,
        values[fieldSettingId],
      )

      if (normalizedNext === normalizedCurrent) return true

      setSavingFieldIds((prev) => ({ ...prev, [fieldSettingId]: true }))

      try {
        const apiTask = await persistTaskTrackerStickerValue(task.id, {
          fieldSettingId,
          fieldType,
          value: normalizedNext,
        })

        const displayValue =
          options?.displayValue !== undefined
            ? normalizeStickerValueForApi(options.displayValue)
            : normalizedNext

        const updatedTask = mergeTaskCardAfterStickerSave(
          task,
          apiTask,
          fieldSettingId,
          displayValue,
          stickerRow
            ? {
                fieldType: stickerRow.fieldType,
                title: stickerRow.title,
                color: stickerRow.color,
                isVisible: stickerRow.isVisible,
              }
            : undefined,
          options?.crmLeadId !== undefined || options?.crmFunnelId !== undefined
            ? {
                crmLeadId: options.crmLeadId ?? null,
                crmFunnelId: options.crmFunnelId ?? null,
              }
            : undefined,
        )

        setValues(buildSavedValues(updatedTask, visibleRows))
        onTaskUpdated?.(updatedTask)
        return true
      } catch (saveError: unknown) {
        addAlert({
          type: 'error',
          title: 'Ошибка',
          text: getErrorMessage(saveError, 'Не удалось сохранить значение стикера'),
        })
        return false
      } finally {
        setSavingFieldIds((prev) => {
          const next = { ...prev }
          delete next[fieldSettingId]
          return next
        })
      }
    },
    [
      addAlert,
      onTaskUpdated,
      savingFieldIds,
      task,
      values,
      visibleRows,
    ],
  )

  return {
    fieldRows,
    loading,
    error,
    hasVisibleStickers:
      task != null && !loading && !error && visibleRows.length > 0,
    isFieldSaving,
    saveStickerValue,
  }
}
